/* =========================================================================
   sites.ts — Pure site helpers (ZERO React imports).
   CRUD utilities + scenario filtering/grouping by site.
   ========================================================================= */

import type { Scenario } from "./types";
import type { Site } from "./platform-types";

// Sub-millisecond counter keeps rapid createSite calls (e.g. tests, double-clicks)
// from producing duplicate ids while preserving the `site-<ts>` prefix.
let siteIdCounter = 0;

/** Create a new Site with an app-generated id and current timestamps. */
export function createSite(name: string, description?: string): Site {
  const now = Date.now();
  siteIdCounter = (siteIdCounter + 1) % 1_000_000;
  return {
    id: `site-${now}-${siteIdCounter}`,
    name,
    description,
    createdAt: now,
    updatedAt: now,
  };
}

/** Immutably update a site's name/description and bump its updatedAt. */
export function updateSite(
  sites: Site[],
  id: string,
  name: string,
  description?: string,
): Site[] {
  return sites.map((s) =>
    s.id === id ? { ...s, name, description, updatedAt: Date.now() } : s,
  );
}

/** Immutably remove a site by id. */
export function removeSite(sites: Site[], id: string): Site[] {
  return sites.filter((s) => s.id !== id);
}

/** Return scenarios belonging to the given site.
 *  `siteId === null` means "all scenarios" (no filter). */
export function filterScenariosBySite(
  scenarios: Scenario[],
  siteId: string | null,
): Scenario[] {
  if (siteId === null) return scenarios;
  return scenarios.filter((s) => s.siteId === siteId);
}

/** Scenarios that are not assigned to any site. */
export function ungroupedScenarios(scenarios: Scenario[]): Scenario[] {
  return scenarios.filter((s) => !s.siteId);
}

export interface SiteGroup {
  siteId: string | null;
  siteName: string;
  scenarios: Scenario[];
}

/** Group scenarios by site for optgroup rendering. Named sites first,
 *  "Ungrouped" (siteId === null/undefined) always last. */
export function groupScenariosBySite(
  scenarios: Scenario[],
  sites: Site[],
): SiteGroup[] {
  const nameById = new Map(sites.map((s) => [s.id, s.name]));
  const buckets = new Map<string | null, Scenario[]>();

  for (const s of scenarios) {
    const key = s.siteId ?? null;
    const list = buckets.get(key) ?? [];
    list.push(s);
    buckets.set(key, list);
  }

  const groups: SiteGroup[] = [];
  for (const [siteId, list] of buckets) {
    groups.push({
      siteId,
      siteName: siteId ? (nameById.get(siteId) ?? "Unknown site") : "Ungrouped",
      scenarios: list,
    });
  }

  return groups.sort((a, b) => {
    if (a.siteId === null && b.siteId !== null) return 1;
    if (a.siteId !== null && b.siteId === null) return -1;
    return a.siteName.localeCompare(b.siteName);
  });
}
